import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import type { SpartenConfig } from '@/features/Sparten/types';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';

import { useStorableState } from '@/utils/useStorableState/useStorableState';
import {
  type InputStore,
  useHunInputStore,
} from '@/features/Sparten/hun/useInputStore';
import { formatCurrency, formatMillions } from '@/utils/formatters/euro';
import { formatYears } from '@/utils/formatters/years';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import image from './assets/bg.jpg';
import { hunVersicherungsangabenConfig } from '@/features/Sparten/hun/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/hun',

  props: {
    name: 'Hundehalterhaftpflichtversicherung',
    sparte: 'hun',
    image,
  },

  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: hunVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
      },
    },

    profileFields: ['deckungssumme', 'selbstbeteiligung'],

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
      },
    },

    quotePreInputMergeCallback: (data: Record<string, unknown>) => {
      const quoteData = data as InputStore;
      if (Array.isArray(quoteData.hunde)) {
        quoteData.hunde = quoteData.hunde.filter((hund, index) => {
          if (index < quoteData.hundeAnzahl) return true;

          return hund.rasse !== '-1';
        });
      }
      return quoteData;
    },

    buildInputDataObject: () =>
      useStorableState().create(useHunInputStore().$state),

    buildContractDataObject: (state) => state,

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.DECKUNGSSUMME,
      SORTABLE_ATTRIBUTES.SELBSTBETEILIGUNG,
      SORTABLE_ATTRIBUTES.LAUFZEIT,
    ],

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.DECKUNGSSUMME),
          formatter: formatMillions,
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/hun/components/TariffDetails/TariffDetails.vue'
      ),

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/hun/views/InputView.vue'),
    },
    offers: {
      component: () => import('@/features/Sparten/hun/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/hun/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/hun/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
